// CertificatePreviewModal.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Typography,
  Input,
  DatePicker,
  Grid,
  Select,
} from "antd";
import { LeftOutlined, CheckOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { saveCertificate } from "../../services/doctor/certificate/certificate";
import RichTextEditor from "./RichTextEditor";
import { getPrintSettings } from "../../services/doctor/print_settings/printSettings";
import { useAuth } from "../../services/authentication_API/authService";
import { useMessageApi } from "../../services/message api/MessageProvider";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;


const CertificatePreviewModal = ({ openCetificatePreview, onClose, cetificateTitle, formData, cetificatePreviewData, isCertificateEditModalClose }) => {

  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [saveCertificateData, setSaveCertificateData] = useState('');
  const [printCertificateData, setPrintCertificateData] = useState('');
  const printRef = useRef(null);
  const [settings, setSettings] = useState();
  const { userData } = useAuth();
  const messageApi = useMessageApi();

  const addCertificateData = async () => {
    try {
      const result = await saveCertificate({
        ...formData,
        print_html_view: printRef.current.innerHTML,
      });
      // console.log('result api', result);
      if (result.success) {
        // console.log("saved:", result);

        messageApi.success("Certificate Saved Successfully")

        onClose();
        isCertificateEditModalClose(true);
      }
    } catch (err) {
      console.error("Error:", err.message);
    }
  };

  useEffect(() => {
    if( saveCertificateData=='' )
    {
      return;
    }
    
    addCertificateData();
  }, [saveCertificateData, printCertificateData]);

  const fetchPrintSettings = async () => {
    try {
      const result = await getPrintSettings(userData?.id);
      if (result.success) {
        setSettings(result?.data);
      } else {
        console.error(
          "Error:",
          result.error || "Failed to fetch Print Settings"
        );
      }
    } catch (err) {
      console.error("Exception:", err.message);
    }
  };

  useEffect(() => {
    fetchPrintSettings();
  }, [openCetificatePreview]);

  // ------------ HEADER ------------
  const customHeader = (
    <div
      className="mb-4"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 24px",
        backgroundColor: "#00bfff",
        color: "white",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <LeftOutlined
          style={{ fontSize: "24px", cursor: "pointer", marginRight: "16px" }}
          onClick={onClose}
        />
      </div>

      <Button
        type="primary"
        ghost
        icon={<CheckOutlined />}
        style={{ color: "white", borderColor: "white" }}
        onClick={onClose}
      >
        Edit
      </Button>
    </div>
  );

  // ✅ CSS Print Media Query Approach (Most Reliable)
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open(
      "about:blank", // THIS FIXES SAFARI
      "printWin",
      "popup=yes,width=800,height=900"
    );
    printWindow.document.write(`
    <html>
      <head>
        <title>Prescription</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no" />
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
          crossorigin="anonymous"
        />
        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }
          body, table, div, span, p {
            font-size: 16px !important;
          }

          html, body {
            width: 210mm;
            height: auto;
            margin: 0;
            padding: 0;
            background: white;
            overflow: hidden;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-wrapper {
            width: 100vw;
            max-width: 210mm;
            margin: 0 auto;
            box-sizing: border-box;
            padding: ${settings.prescription_print_header_margin || 0}% ${
              settings.prescription_print_right_margin || 0
            }% ${settings.prescription_print_footer_margin || 0}% ${
              settings.prescription_print_left_margin || 0
            }%;
            background: white;
            transform-origin: top center;
          }

          table {
            width: 100%;
            border-collapse: collapse;

          }
            .thick-border table,
            .thick-border th,
            .thick-border td {
            border-width: 0.5px !important;
            border-color: #2c2c2cff !important;
          }

           td {
            border: 2px solid #ddd;
            padding: 8px;
            font-size: 16px;
          }

          th {
            background: #f8f9fa;
            text-align: left;
            border: 2px solid #ddd;
            padding: 8px;
            font-size: 16px;

          }

          .no-print { display: none !important; }
          hr { border: 0; border-top: 1px solid #ccc; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div id="print-area" class="print-wrapper">
          ${printContents}
        </div>
        <script>
          window.onload = function() {
            const content = document.getElementById('print-area');
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

            // Get A4 height in pixels at 96 DPI
            const A4_HEIGHT_PX = 1122; // 297mm ≈ 1122px
            const contentHeight = content.scrollHeight;

            // If content overflows one A4 page, scale it down
            if (contentHeight > A4_HEIGHT_PX) {
              const scale = A4_HEIGHT_PX / contentHeight;
              content.style.transform = 'scale(' + scale + ')';
              content.style.transformOrigin = 'top center';
              document.body.style.height = A4_HEIGHT_PX + 'px';
            } else if (isIOS) {
              // Avoid unnecessary downscaling (causes side gaps)
              content.style.transform = 'scale(1)';
              content.style.transformOrigin = 'top center';
              document.body.style.margin = '0';
              document.body.style.padding = '0';
              content.style.width = '100vw';
            }


            setTimeout(() => {
              window.focus();
              window.print();
              let attempts = 0;

            function tryClose() {
              try {
                window.close();

                if (!window.closed && attempts < 10) {
                  attempts++;
                  setTimeout(tryClose, 300);
                }
              } catch (e) {
                if (attempts < 10) {
                  attempts++;
                  setTimeout(tryClose, 300);
                }
              }
            }

            window.onafterprint = tryClose;

            // failsafe auto close after 1 second
            setTimeout(tryClose, 1000);

            }, 500);
          };
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        let attempts = 0;
        function tryClose() {
          try {
            printWindow.close();
            if (!printWindow.closed && attempts < 10) {
              attempts++;
              setTimeout(tryClose, 400);
            }
          } catch {
            if (attempts < 10) {
              attempts++;
              setTimeout(tryClose, 400);
            }
          }
        }

        // Safari: onafterprint rarely works, but include it
        printWindow.onafterprint = tryClose;

        // Fallback close
        setTimeout(tryClose, 1500);
      }, 300);
    };
  };
  
  const testPopup = () => {
    const html = printRef.current.innerHTML;

    // Remove old iframe
    const oldIframe = document.getElementById("print-iframe");
    if (oldIframe) oldIframe.remove();

    // Create iframe (visible but transparent)
    const iframe = document.createElement("iframe");
    iframe.id = "print-iframe";
    iframe.style.position = "fixed";
    iframe.style.top = "0";
    iframe.style.left = "0";
    iframe.style.width = "100vw";
    iframe.style.height = "100vh";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.zIndex = "-1";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;

    doc.open();
    doc.write(`
    <html>
      <head>
        <title>Prescription</title>
        
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no"/>

        <link 
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
          crossorigin="anonymous"
        />

        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }

          body, table, div, span, p {
            font-size: 16px !important;
          }

          html, body {
            width: 210mm;
            height: auto;
            margin: 0;
            padding: 0;
            background: white;
            overflow: hidden;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-wrapper {
            width: 100vw;
            max-width: 210mm;
            margin: 0 auto;
            box-sizing: border-box;
            padding: ${settings.prescription_print_header_margin || 0}% 
                     ${settings.prescription_print_right_margin || 0}% 
                     ${settings.prescription_print_footer_margin || 0}% 
                     ${settings.prescription_print_left_margin || 0}%;
            background: white;
            transform-origin: top center;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .thick-border table,
          .thick-border th,
          .thick-border td {
            border-width: 0.5px !important;
            border-color: #2c2c2cff !important;
          }

          td {
            border: 2px solid #ddd;
            padding: 8px;
            font-size: 16px;
          }

          th {
            background: #f8f9fa;
            text-align: left;
            border: 2px solid #ddd;
            padding: 8px;
            font-size: 16px;
          }

          .no-print { display: none !important; }

          hr { 
            border: 0; 
            border-top: 1px solid #ccc; 
            margin: 20px 0; 
          }
        </style>
      </head>

      <body>
        <div class="print-wrapper">
          ${html}
        </div>
      </body>
    </html>
  `);

    doc.close();

    // iPad Safari + Chrome iOS wait logic
    const waitUntilReady = () => {
      const state = doc.readyState;

      if (state === "complete" || state === "interactive") {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          });
        });
      } else {
        setTimeout(waitUntilReady, 50);
      }
    };

    waitUntilReady();
  };

  const isIpadSafari = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // iPad Safari = iPad + WebKit + NOT Chrome
    const isIpad =
      /iPad/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isWebkit = /WebKit/.test(ua);
    const isChrome = /CriOS/.test(ua); // Chrome on iOS

    return isIpad && isWebkit && !isChrome;
  };

  return (
    <>
      <Modal
        title={null}
        open={openCetificatePreview}
        onCancel={onClose}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "15px" }}>
            <Button 
              onClick={() => {
                  setSaveCertificateData(true);
                }}
              >
                Save & Exit
            </Button>
            <Button type="primary"
              onClick={() => {
                try {
                  if (isIpadSafari()) {
                    testPopup(); // ✔ works on iPad Safari
                  } else {
                    handlePrint(); // ✔ all other browsers
                  }
                  setPrintCertificateData(true);
                  setSaveCertificateData(true);
                } catch (err) {
                  console.error("Print failed:", err);
                }
              }}
            >
              Save & Print
            </Button>
          </div>
        }
        width={screens.lg ? 900 : "90%"}
        maskClosable={false}
        styles={{ body: { padding: 0 } }}
        closeIcon={null}
        style={{ top: 0 }}
      >
        {customHeader}

        <div
          ref={printRef}
          id="print-area"
          className="bg-white rounded shadow-sm border p-4"
          style={{ position: "relative" }}   // 👈 important
        >
            {/* Date – Top Right */}
          <div style={{ textAlign: "end", marginBottom: "10px" }}>
            <h1 style={{ fontWeight: "", fontSize: "16px" }}>Date: {dayjs().format("DD-MM-YYYY")}</h1>
          </div>
      
          {/* Title */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <h1 style={{ fontWeight: "bold", fontSize: "24px" }}>{cetificateTitle}</h1>
          </div>
      
          {/* Certificate Body */}
          <div style={{ fontSize: "18px", lineHeight: "2" }}>
            <div
              style={{ marginBottom: "20px" }}
              dangerouslySetInnerHTML={{ __html: cetificatePreviewData }}
            />
          </div>
      
          <hr style={{ marginBottom: "20px" }} />
      
          {/* Footer */}
          <div className="d-flex justify-content-between align-items-end">
            <div></div>
            <div className="text-end">
              <div className="text-start">
                {settings?.doctor_signature_image_url && (
                  <div className="mt-2 text-center" style={{ maxWidth: "200px" }}>
                    <img
                      src={settings.doctor_signature_image_url}
                      alt="Signature Preview"
                      style={{ width: "100%", height: "auto", objectFit: "contain" }}
                    />
                  </div>
                )}
                <div className="fw-medium">
                  {userData.name} {userData?.last_name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

CertificatePreviewModal.propTypes = {
  openCetificatePreview: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cetificateTitle: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  cetificatePreviewData: PropTypes.string.isRequired,
  isCertificateEditModalClose: PropTypes.func.isRequired,
};

export default CertificatePreviewModal;
